import React, { Suspense, useEffect, useRef, useState } from 'react'
import { BrowserRouter, Routes, Route, Link, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { I18nProvider, useT } from './i18n/index.js'
import { OfflineIndicator } from './components/OfflineIndicator.js'
import { ToastProvider } from './components/Toast.js'
import { ErrorBoundary } from './components/ErrorBoundary.js'
import { useOnlineStatus } from './hooks/useOffline.js'
import { getOfflineActions, clearOfflineActions, removeOfflineAction } from './lib/offlineStorage.js'
import { DemoBanner } from './components/DemoBanner.js'
import { AuthProvider, useAuth } from './hooks/useAuth.js'
import { apiFetch } from './hooks/useApiBase.js'

// ── Lazy-loaded pages (UX-PERF-04/05: code splitting par route) ──
const LandingPage = React.lazy(() => import('./pages/LandingPage.js').then(m => ({ default: m.LandingPage })))
const DashboardPage = React.lazy(() => import('./pages/DashboardPage.js').then(m => ({ default: m.DashboardPage })))
const SitesPage = React.lazy(() => import('./pages/SitesPage.js').then(m => ({ default: m.SitesPage })))
const WasteEntryPage = React.lazy(() => import('./pages/WasteEntryPage.js').then(m => ({ default: m.WasteEntryPage })))
const BordereauxPage = React.lazy(() => import('./pages/BordereauxPage.js').then(m => ({ default: m.BordereauxPage })))
const BordereauDetailPage = React.lazy(() => import('./pages/BordereauDetailPage.js').then(m => ({ default: m.BordereauDetailPage })))
const ReportsPage = React.lazy(() => import('./pages/ReportsPage.js').then(m => ({ default: m.ReportsPage })))
const CompliancePage = React.lazy(() => import('./pages/CompliancePage.js').then(m => ({ default: m.CompliancePage })))
const BillingPage = React.lazy(() => import('./pages/BillingPage.js').then(m => ({ default: m.BillingPage })))
const SettingsPage = React.lazy(() => import('./pages/SettingsPage.js').then(m => ({ default: m.SettingsPage })))
const FaqPage = React.lazy(() => import('./pages/FaqPage.js').then(m => ({ default: m.FaqPage })))
const CollectorsPage = React.lazy(() => import('./pages/CollectorsPage.js').then(m => ({ default: m.CollectorsPage })))
const CrossTenantInboxPage = React.lazy(() => import('./pages/CrossTenantInboxPage.js').then(m => ({ default: m.CrossTenantInboxPage })))
const CeoDashboardPage = React.lazy(() => import('./pages/CeoDashboardPage.js').then(m => ({ default: m.CeoDashboardPage })))
const OnboardingPage = React.lazy(() => import('./pages/OnboardingPage.js').then(m => ({ default: m.OnboardingPage })))
const AuditorPortalPage = React.lazy(() => import('./pages/AuditorPortalPage.js').then(m => ({ default: m.AuditorPortalPage })))
const LoginPage = React.lazy(() => import('./pages/LoginPage.js').then(m => ({ default: m.LoginPage })))
const EwcGlossary = React.lazy(() => import('./components/EwcGlossary.js').then(m => ({ default: m.EwcGlossary })))
const GlossaryPage = React.lazy(() => import('./pages/GlossaryPage.js').then(m => ({ default: m.GlossaryPage })))
const ChangelogPage = React.lazy(() => import('./pages/ChangelogPage.js').then(m => ({ default: m.ChangelogPage })))
const EwcAdminPage = React.lazy(() => import('./pages/EwcAdminPage.js').then(m => ({ default: m.EwcAdminPage })))
const NonConformityPage = React.lazy(() => import('./pages/NonConformityPage.js').then(m => ({ default: m.NonConformityPage })))
const NonConformityDetailPage = React.lazy(() => import('./pages/NonConformityDetailPage.js').then(m => ({ default: m.NonConformityDetailPage })))
const SiteDetailPage = React.lazy(() => import('./pages/SiteDetailPage.js').then(m => ({ default: m.SiteDetailPage })))
const CollectorDetailPage = React.lazy(() => import('./pages/CollectorDetailPage.js').then(m => ({ default: m.CollectorDetailPage })))
const GdprSettingsPage = React.lazy(() => import('./pages/GdprSettingsPage.js').then(m => ({ default: m.GdprSettingsPage })))
const FeatureFlagsPage = React.lazy(() => import('./pages/FeatureFlagsPage.js').then(m => ({ default: m.FeatureFlagsPage })))
const RoundsPage = React.lazy(() => import('./pages/RoundsPage.js').then(m => ({ default: m.RoundsPage })))
const DriverDashboardPage = React.lazy(() => import('./pages/DriverDashboardPage.js').then(m => ({ default: m.DriverDashboardPage })))
const VehiclesPage = React.lazy(() => import('./pages/VehiclesPage.js').then(m => ({ default: m.VehiclesPage })))
const TeamPage = React.lazy(() => import('./pages/TeamPage.js').then(m => ({ default: m.TeamPage })))
const DiwassPage = React.lazy(() => import('./pages/DiwassPage.js').then(m => ({ default: m.DiwassPage })))
const VerifyPage = React.lazy(() => import('./pages/VerifyPage.js').then(m => ({ default: m.VerifyPage })))
const MentionsLegalesPage = React.lazy(() => import('./pages/MentionsLegalesPage.js').then(m => ({ default: m.MentionsLegalesPage })))
const PolitiqueConfidentialitePage = React.lazy(() => import('./pages/PolitiqueConfidentialitePage.js').then(m => ({ default: m.PolitiqueConfidentialitePage })))
const CgvPage = React.lazy(() => import('./pages/CgvPage.js').then(m => ({ default: m.CgvPage })))
const PWAInstallPrompt = React.lazy(() => import('./components/PWAInstallPrompt.js'))
const AIChatPanel = React.lazy(() => import('./components/AIChatPanel.js').then(m => ({ default: m.AIChatPanel })))
const PwaUpdatePrompt = React.lazy(() => import('./components/PwaUpdatePrompt.js').then(m => ({ default: m.PwaUpdatePrompt })))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 30_000,
    },
  },
})

// ── Offline sync orchestrator ──────────────────────────────────────────────

type SyncState = 'idle' | 'syncing' | 'done' | 'error'

/**
 * Sits above the Router so it has access to the online status hook.
 * When connectivity is restored and there are queued offline actions, it
 * posts them to POST /api/sync and invalidates React Query caches.
 */
const OfflineSyncManager: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { wasOffline } = useOnlineStatus()
  const [syncState, setSyncState] = useState<SyncState>('idle')
  // Guard against double-firing
  const syncing = useRef(false)

  useEffect(() => {
    if (!wasOffline || syncing.current) return

    const runSync = async () => {
      const actions = await getOfflineActions()
      if (actions.length === 0) return

      syncing.current = true
      setSyncState('syncing')

      try {
        const result = await apiFetch<{
          ok: boolean
          synced: number
          failed: number
          results: Array<{ id: string; success: boolean }>
        }>('/api/sync', {
          method: 'POST',
          body: JSON.stringify({ actions }),
        })

        // Remove only the actions that succeeded
        for (const r of result.results) {
          if (r.success) {
            await removeOfflineAction(r.id)
          }
        }

        // If everything succeeded, clear the whole store (belt-and-suspenders)
        if (result.failed === 0) {
          await clearOfflineActions()
        }

        // Invalidate all queries so the UI refreshes with server data
        await queryClient.invalidateQueries()

        setSyncState('done')
      } catch {
        setSyncState('error')
      } finally {
        syncing.current = false
        // Reset to idle after a short delay
        setTimeout(() => setSyncState('idle'), 6_000)
      }
    }

    runSync().catch(() => {
      setSyncState('error')
      syncing.current = false
    })
  }, [wasOffline])

  return (
    <>
      <OfflineIndicator syncState={syncState} />
      {children}
    </>
  )
}

// ── Auth guard — redirects to /login if not authenticated ──
const AuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth()
  const t = useT()

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin motion-reduce:animate-none" />
          <span className="text-sm text-gray-500">{t('common.loading')}</span>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

// ── App ────────────────────────────────────────────────────────────────────

export const App: React.FC = () => (
  <ErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <I18nProvider>
      <AuthProvider>
      <ToastProvider>
      <OfflineSyncManager>
        <DemoBanner />
        <BrowserRouter>
          <Suspense fallback={<PageSkeleton />}>
          <Routes>
            {/* Public pages */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/glossaire-ewc" element={<EwcGlossary />} />
            <Route path="/glossaire" element={<GlossaryPage />} />
            <Route path="/verify/:hash" element={<VerifyPage />} />
            <Route path="/changelog" element={<ChangelogPage />} />
            <Route path="/legal" element={<MentionsLegalesPage />} />
            <Route path="/privacy" element={<PolitiqueConfidentialitePage />} />
            <Route path="/terms" element={<CgvPage />} />
            <Route path="/admin/ewc-codes" element={<AuthGuard><EwcAdminPage /></AuthGuard>} />
            <Route path="/non-conformities" element={<AuthGuard><NonConformityPage /></AuthGuard>} />
            <Route path="/non-conformities/:id" element={<AuthGuard><NonConformityDetailPage /></AuthGuard>} />
            <Route path="/settings/privacy" element={<AuthGuard><GdprSettingsPage /></AuthGuard>} />
            <Route path="/settings/feature-flags" element={<AuthGuard><FeatureFlagsPage /></AuthGuard>} />
            <Route path="/rounds" element={<AuthGuard><RoundsPage /></AuthGuard>} />
            <Route path="/driver" element={<AuthGuard><DriverDashboardPage /></AuthGuard>} />
            <Route path="/vehicles" element={<AuthGuard><VehiclesPage /></AuthGuard>} />

            {/* Onboarding */}
            <Route path="/onboarding" element={<AuthGuard><OnboardingPage /></AuthGuard>} />

            {/* App pages (auth required) */}
            <Route path="/dashboard" element={<AuthGuard><DashboardPage /></AuthGuard>} />
            <Route path="/dashboard/ceo" element={<AuthGuard><CeoDashboardPage /></AuthGuard>} />
            <Route path="/sites" element={<AuthGuard><SitesPage /></AuthGuard>} />
            <Route path="/sites/:id" element={<AuthGuard><SiteDetailPage /></AuthGuard>} />
            <Route path="/waste-entry" element={<AuthGuard><WasteEntryPage /></AuthGuard>} />
            <Route path="/bordereaux" element={<AuthGuard><BordereauxPage /></AuthGuard>} />
            <Route path="/bordereaux/:id" element={<AuthGuard><BordereauDetailPage /></AuthGuard>} />
            <Route path="/reports" element={<AuthGuard><ReportsPage /></AuthGuard>} />
            <Route path="/compliance" element={<AuthGuard><CompliancePage /></AuthGuard>} />
            <Route path="/settings/billing" element={<AuthGuard><BillingPage /></AuthGuard>} />
            <Route path="/settings" element={<AuthGuard><SettingsPage /></AuthGuard>} />
            <Route path="/faq" element={<AuthGuard><FaqPage /></AuthGuard>} />
            <Route path="/collectors" element={<AuthGuard><CollectorsPage /></AuthGuard>} />
            <Route path="/collectors/:id" element={<AuthGuard><CollectorDetailPage /></AuthGuard>} />
            <Route path="/inbox" element={<AuthGuard><CrossTenantInboxPage /></AuthGuard>} />
            <Route path="/bsd-inbox" element={<Navigate to="/inbox" replace />} />
            <Route path="/team" element={<AuthGuard><TeamPage /></AuthGuard>} />
            <Route path="/diwass" element={<AuthGuard><DiwassPage /></AuthGuard>} />

            {/* External auditor portal (read-only, token-based) */}
            <Route path="/auditor" element={<AuditorPortalPage />} />

            {/* Admin — @saas/admin shell (future, not in MVP scope) */}

            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
        <Suspense fallback={null}>
          <AIChatPanel />
          <PWAInstallPrompt />
          <PwaUpdatePrompt />
        </Suspense>
      </OfflineSyncManager>
      </ToastProvider>
      </AuthProvider>
    </I18nProvider>
  </QueryClientProvider>
  </ErrorBoundary>
)

// ── Skeleton for Suspense fallback (UX-PERF-06) ──
const PageSkeleton: React.FC = () => {
  const t = useT()
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin motion-reduce:animate-none" />
        <span className="text-sm text-gray-500">{t('common.loading')}</span>
      </div>
    </div>
  )
}

const NotFound: React.FC = () => {
  const t = useT()
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
        <p className="text-gray-600 mb-6">{t('common.pageNotFound')}</p>
        <Link to="/" className="text-primary hover:text-primary-hover font-medium focus:ring-2 focus:ring-primary focus:ring-offset-1 rounded">
          &larr; {t('common.backToHome')}
        </Link>
      </div>
    </div>
  )
}
